import { useState, useEffect, useRef } from "react";
import styled, { keyframes } from "styled-components";
import classNames from "classnames";
import { motion } from "motion/react";

export default function Squiggle({
  color,
  show = false,
  tabKey,
  anyOtherTabIsOpen,
}) {
  const pathRef = useRef();
  const [isAnimating, setIsAnimating] = useState(false);
  const [isShowing, setIsShowing] = useState(false);
  const [isErasing, setIsErasing] = useState(false);

  useEffect(() => {
    const onAnimationEnd = () => {
      setIsAnimating(false);
    };
    pathRef.current?.addEventListener("animationend", onAnimationEnd);
  }, []);

  useEffect(() => {
    if (show && !isAnimating && !isShowing) {
      setIsErasing(false);
      setIsAnimating(true);
      setIsShowing(true);
    }
  }, [show, isAnimating]);

  useEffect(() => {
    if (!show && !isAnimating && isShowing) {
      setIsShowing(false);
      setIsErasing(true);
      setIsAnimating(true);
    }
  }, [show, isAnimating]);

  const tabsData = {
    database: {
      width: 200,
      path: "M10,20 C22,10 32,30 44,20 C56,10 66,30 78,20 C90,10 100,30 112,20 C124,10 134,30 146,20 C158,10 168,30 180,20 C185,15 188,25 190,20",
      strokeDasharray: 240,
      marginLeft: 2,
    },
    groups: {
      width: 240,
      path: "M10,20 C26,10 38,30 54,20 C70,10 82,30 98,20 C114,10 126,30 142,20 C158,10 170,30 186,20 C202,10 214,30 230,20",
      strokeDasharray: 280,
      marginLeft: -14,
    },
    giving: {
      width: 80,
      path: "M5,20 C12,10 18,30 25,20 C32,10 38,30 45,20 C52,10 58,30 65,20 C70,15 75,25 75,20",
      strokeDasharray: 100,
      marginLeft: -10,
    },
    signups: {
      width: 180,
      path: "M10,20 C16,10 22,30 28,20 C34,10 40,30 46,20 C52,10 58,30 64,20 C70,10 76,30 82,20 C88,10 94,30 100,20 C106,10 112,30 118,20 C124,10 130,30 136,20 C142,10 148,30 154,20 C160,10 166,30 170,20",
      strokeDasharray: 240,
      marginLeft: 6,
    },
    tools: {
      width: 160,
      path: "M10,20 C20,10 28,30 38,20 C48,10 56,30 66,20 C76,10 84,30 94,20 C104,10 112,30 122,20 C132,10 140,30 150,20",
      strokeDasharray: 200,
      marginLeft: -10,
    },
  };
  if (!tabsData[tabKey]) return null;

  return (
    <Wrapper style={{ marginLeft: tabsData[tabKey].marginLeft || null }}>
      <Container animate={{ scale: anyOtherTabIsOpen ? 0.8 : 1 }}>
        <svg
          width={tabsData[tabKey].width}
          height={40}
          viewBox={`0 0 ${tabsData[tabKey].width} 40`}
        >
          <Path
            ref={pathRef}
            d={tabsData[tabKey].path}
            style={{
              stroke: color,
              "--strokeDasharray": tabsData[tabKey].strokeDasharray,
              "--strokeDashoffsetNegative":
                0 - tabsData[tabKey].strokeDasharray,
            }}
            className={classNames({ show: isShowing, hide: isErasing })}
          />
        </svg>
      </Container>
    </Wrapper>
  );
}
const Wrapper = styled.div`
  position: absolute;
  bottom: 0;
  left: 58px;
  width: 200px;
  height: 20px;
`;
const Container = styled(motion.div)`
  transform-origin: 0 0%;
`;

const drawLine = keyframes`
	50% {
		opacity:1;
	}
	100% {
		stroke-dashoffset: 0;
		opacity:1;
	}
`;
const eraseLine = keyframes`
	0% {
		stroke-dasharray: var(--strokeDasharray) var(--strokeDasharray);
		stroke-dashoffset: 0;
		opacity:1;
	}
	25% {
		opacity:1;
	}
	100% {
		stroke-dasharray: var(--strokeDasharray) var(--strokeDasharray);
		stroke-dashoffset: var(--strokeDashoffsetNegative);
		opacity:0;
	}
`;

const Path = styled.path`
  stroke: #3498db;
  stroke-width: 3px;
  stroke-linecap: round;
  stroke-linejoin: round;
  fill: none;
  transition: stroke 0.4s;
  stroke-dasharray: var(--strokeDasharray);
  stroke-dashoffset: var(--strokeDasharray);
  opacity: 0;
  &.show {
    animation: ${drawLine} 0.5s ease-out forwards;
  }
  &.hide {
    animation: ${eraseLine} 0.4s ease-out forwards;
  }
`;
